import { useCallback, useEffect, useState } from "react";
import { DollarSign } from "lucide-react";
import { adminApi } from "@/lib/api";

const money = (n) => `$${(n || 0).toFixed(2)}`;
const hrs = (h) => {
  const m = Math.round((h || 0) * 60);
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};
const centralToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago" }).format(new Date());

export function DailyPayCard({ token, refreshKey }) {
  const [date, setDate] = useState(centralToday());
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi(token).get(`/admin/labor/day?date=${date}`);
      setData(data);
    } catch {}
  }, [token, date]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load, refreshKey]);

  const isToday = date === centralToday();
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="daily-pay-card">
      <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-600" /> {isToday ? "Pay owed today" : "Pay owed"}
          </div>
          <div className="text-[11px] text-stone-500">Hours worked × pay rate, unpaid breaks already taken out. Live while they&apos;re clocked in.</div>
        </div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="daily-pay-date"
          className="px-3 py-1.5 rounded-lg border border-stone-300 text-sm font-mono" />
      </div>
      {!data ? (
        <div className="p-6 text-center text-stone-400 text-sm">Loading…</div>
      ) : data.employees.length === 0 ? (
        <div className="p-6 text-center text-stone-400 text-sm" data-testid="daily-pay-empty">No hours worked on this day.</div>
      ) : (
        <>
          <ul className="divide-y divide-stone-100">
            {data.employees.map((r) => (
              <li key={r.employee_id || r.name} className="px-5 py-3 flex items-center gap-3" data-testid={`daily-pay-row-${r.employee_id || r.name}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-800 text-sm flex items-center gap-2 flex-wrap">
                    {r.name}
                    {r.on_clock && (
                      <span className={`text-[9px] uppercase tracking-widest rounded-full px-1.5 py-0.5 ${r.on_break ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                        {r.on_break ? "On break" : "On the clock"}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-stone-500">
                    {hrs(r.hours)} × {money(r.pay_rate)}/hr
                    {r.break_hours > 0 && ` · ${hrs(r.break_hours)} break off`}
                  </div>
                </div>
                <div className="font-mono font-black text-emerald-700 text-base" data-testid={`daily-pay-amount-${r.employee_id || r.name}`}>{money(r.pay)}</div>
              </li>
            ))}
          </ul>
          <div className="px-5 py-3 bg-stone-50 border-t border-stone-200 flex items-center justify-between" data-testid="daily-pay-total">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Total · {hrs(data.total_hours)}</div>
            <div className="font-mono font-black text-stone-900 text-lg">{money(data.total_pay)}</div>
          </div>
        </>
      )}
    </div>
  );
}
